import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subscription } from "rxjs";
import { DateTimeRegex } from "src/components/shared-components/class/general-classes/date-time-regex";
import { ItemHistory } from "src/components/shared-components/class/general-classes/item-history";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { NetworkService } from "src/service/network.service";

@Component({
    selector: "app-item-history-list",
    templateUrl: "./item-history-list.component.html",
    styleUrls: ["./item-history-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ItemHistoryListComponent
  implements OnInit, OnDestroy, iConnToolbarColour, iTranslation, OnChanges {
  @Input() itemHistoryList: ItemHistory[] = [];
  @Input() select: boolean = false;
  @Output() selectedEmitter = new EventEmitter<number>();

  private _isLoading: boolean = true;
  private _numElementInScroll: number = 15;
  private _pageScroll: number = 0;
  private _remaining_items: string = "";
  private _remaining_items_other: string = "";

  private _translateSubscription: Subscription = null;

  private _networkServiceSubscription: Subscription = null;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  public remaining_items: string = "";
  public isScrollVisible: boolean = true;
  public listScroll: Array<ItemHistory> = [];
  public selected: number = -1;

  constructor(
    private _networkService: NetworkService,
    private _translateService: TranslateService
  ) { }

  public ngOnInit(): void {
    this.calculateScrollList();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
    moment.locale(this._translateService.getCurrentLang());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.calculateScrollList();
  }

  public ngOnDestroy(): void {
    this._networkServiceSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.remainingItems", "dictionary.remainingItemsOther"])
      .subscribe((translation) => {
        this._remaining_items = translation["dictionary.remainingItems"];
        this._remaining_items_other =
          translation["dictionary.remainingItemsOther"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  public onClickSelect(index: number): void {
    if (this.select && index >= 0 && index < this.itemHistoryList.length) {
      this.selected = index;
      this.selectedEmitter.emit(index);
    }
  }

  /**
   * Load object into the infinite scroll list
   *
   * @param event
   */
  public doInfinite(event: any): void {
    if (!this._isLoading) {
      this.isScrollVisible = false;
    } else {
      let startArray = this._pageScroll * this._numElementInScroll;
      this._pageScroll++;
      let endArray = this._pageScroll * this._numElementInScroll;

      if (endArray >= this.itemHistoryList.length) {
        endArray = this.itemHistoryList.length;
        this._isLoading = false;
      }
      setTimeout(() => {
        this.listScroll.push(
          ...this.itemHistoryList.slice(startArray, endArray)
        );
        event.target.complete();
      }, 500);

      this.calculateRemaingItemsSearch();
    }
  }

  /**
   * Populate list after load items
   */
  private calculateScrollList(): void {
    this.listScroll = [];
    this.populateListScrollSearch();
  }

  /**
   * Calculate elements not loaded yet
   */
  private calculateRemaingItemsSearch(): void {
    const elementToLoad = this.itemHistoryList.length - this.listScroll.length;
    this.remaining_items =
      this._remaining_items_other +
      " " +
      elementToLoad.toString() +
      " " +
      this._remaining_items;
  }

  /**
   * Check if text is a date, if is it change format date
   */
  public checkIfIsDate(text: string): string {
    let testdata = text.split("/").join("").substring(0, 6);
    var dateFormat = "DD/MM/YY hh:mm:ss";
    if (
      new RegExp(DateTimeRegex.DATE_ddmmyy_NO_SEPARATION_REGEXEXPR).test(
        testdata
      )
    ) {
      if (moment(text, dateFormat).isValid()) {
        return moment(text, "DD/MM/YY HH:mm:ss")
          .format("DD MMMM YYYY   HH:mm:ss")
          .toString();
      } else {
        return text;
      }
    } else {
      return text;
    }
  }

  /**
   * Handle the load of list to scroll
   */
  private populateListScrollSearch(): void {
    if (this.itemHistoryList.length < this._numElementInScroll) {
      this.listScroll = this.itemHistoryList.slice(0);
      this._isLoading = false;
    } else {
      this.listScroll = this.itemHistoryList.slice(0, this._numElementInScroll);
      this._isLoading = true;
      this.isScrollVisible = true;
    }
    this._pageScroll = 1;
    this.calculateRemaingItemsSearch();
  }

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }
}
